import React, { useState } from "react";
import "./index.css";
import { LuFilePen } from 'react-icons/lu';
import { LuEye } from 'react-icons/lu';
import { LuTrash2 } from 'react-icons/lu';
import { LuHardDriveDownload } from 'react-icons/lu';

const SimpleTable = ({
  columns,
  data,
  onEdit,
  onView,
  onDelete,
  onDownload,
  download = false,
  view = true,              
  edit = true,
  deleteAction = true,
  isLoading = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const rowsPerPage = 8;

  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === "string") {
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
  });

  const filteredData = sortedData.filter((row) =>
    columns.some(
      (column) =>
        column.key !== "action" &&
        row[column.key] &&
        row[column.key].toString().toLowerCase().includes(filterText.toLowerCase())
    )
  );

  const totalRows = filteredData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const pages = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  const handleSort = (key) => {
    if (key === "action") return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  const renderSkeletonRows = () =>
    Array.from({ length: rowsPerPage }).map((_, rowIndex) => (
      <tr key={`sk-${rowIndex}`}>
        {columns.map((_, colIndex) => (
          <td key={colIndex}>
            <div className="skeleton-line" />
          </td>
        ))}
      </tr>
    ));

  return (
    <div className="table-container">
      <div className="filter-container">
        <input
          type="text"
          placeholder="Search..."
          value={filterText}
          onChange={(e) => {
            setFilterText(e.target.value);
            setCurrentPage(1);
          }}
          className="filter-input"
          disabled={isLoading}
        />
      </div>

      <table className="reusable-table">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                onClick={() => handleSort(column.key)}
                className={column.key !== "action" ? "sortable" : ""}
              >
                <div className="header-content">
                  {column.header}
                  {!isLoading && sortConfig.key === column.key && (
                    <span className="sort-icon">
                      {sortConfig.direction === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {isLoading
            ? renderSkeletonRows()
            : currentData.length > 0
              ? currentData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((column, colIndex) => (
                    <td key={colIndex}>
                      {column.key === "action" ? (
                        <div className="action-buttons">
                          {edit && (
                            <LuFilePen
                              className="action-icon edit-icon"
                              onClick={() => onEdit(row.id, row)}
                              title="Edit"
                            />
                          )}
                          {view && (
                            <LuEye
                              className="action-icon view-icon"
                              onClick={() => onView(row.id, row)}
                              title="View"
                            />
                          )}
                          {deleteAction && (
                            <LuTrash2
                              className="action-icon delete-icon"
                              onClick={() => onDelete(row.id, row)}
                              title="Delete"
                            />
                          )}
                          {download && (
                            <LuHardDriveDownload
                              className="action-icon delete-icon"
                              onClick={() => onDownload(row.id, row)}
                              title="download"
                            />
                          )}
                        </div>
                      ) : (
                        row[column.key]
                      )}
                    </td>
                  ))}
                </tr>
              ))
              : (
                <tr>
                  <td colSpan={columns.length} className="no-data">
                    No data found
                  </td>
                </tr>
              )}
        </tbody>
      </table>

      {!isLoading && totalRows > 0 && (
        <div className="pagination-container">
          <button
            className="pagination-button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {getPageNumbers().map((page) => (
            <button
              key={page}
              className={`pagination-button ${page === currentPage ? "active" : ""}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
        </div>
      )}
    </div>
  );
};

export default SimpleTable;
